/**
 * dialogue — «Диалог»: приветствие, BANT-квалификация, возражения, техника
 * закрытия. Зеркалит DialogueSection.tsx для ДАННЫХ и гейтов (BANT-строки —
 * через `buildBantRows`, импортированную оттуда же, не переписанную; гейт
 * видимости секции — `hasBant || hasDialogueDetails`, ровно как на экране).
 *
 * Порядок блоков внутри карточки (приветствие → BANT → возражения →
 * закрытие) следует тексту брифа и согласованному прототипу
 * (`docs/plans/2026-08-06-pdf-report-preview.html`), а не порядку JSX на
 * экране (там BANT идёт первым, отдельным блоком до общего «Dialogue
 * Details»): это доказуемо порядок вёрстки, а не поведение (какие данные
 * показывать, когда) — оба источника, независимо друг от друга, сошлись на
 * приветствии первым.
 *
 * Раскраска бейджа BANT «не определено» — `muted` (серый), а не `danger`
 * (красный) экрана: печатная копия уходит клиенту на руки и не должна
 * тревожно подсвечивать «сроки ещё не обсуждались» так же, как «клиент
 * бросил трубку». Тот же приглушённый регистр уже выбран в этом же модуле
 * для null-балла закрытия (`nullableScore`) и для непроверенных пунктов
 * комплаенса — и совпадает с примером BANT в согласованном прототипе.
 *
 * Все четыре блока карточки («Приветствие», «BANT», «Возражения»,
 * «Закрытие») имеют собственный `.lbl`-заголовок — единообразно, по
 * согласованному прототипу (`preview.html:199-221`). Три из четырёх
 * собраны через примитив `block()`: заголовок — обычный текстовый ключ, без
 * подмешанного HTML. «Возражения» — исключение, собраны вручную: в
 * заголовок вклеивается `scoreSpan(averageScore)` (готовый `<span>`), а
 * `block()` экранировал бы его целиком через `escapeHtml`, превратив тег в
 * текст.
 */
import { isObjectionHandled } from '@aica/core';
import { card, block, kvRow, tag, scoreSpan, nullableScore } from '../primitives';
import { scoreHex } from '../tokens';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';
import { buildBantRows } from '@/components/details/v2/analysis/analysis-utils';

export const renderDialogue: SectionFn = (input, t) => {
  const { result } = input.analysis;
  const dialogue = result.dialogue;

  const bantRows = buildBantRows(dialogue?.needsQualification?.bant, t);
  const hasBant = bantRows.length > 0;

  const greeting = dialogue?.greetingIntro;
  const objections = dialogue?.objectionHandling?.objections;
  const hasObjections = !!objections && objections.length > 0;
  const closing = dialogue?.closingTechnique;
  const hasDialogueDetails = !!(greeting || hasObjections || closing);

  if (!hasBant && !hasDialogueDetails) return '';

  // Without a score the verdict row goes too (#2525): "used" is read off the
  // score, and an empty `.kv` would print as a blank ruled line.
  const greetingVerdict =
    (greeting?.used !== undefined
      ? tag(
          greeting.used
            ? t('records.analysisPanel.dialogue.used')
            : t('records.analysisPanel.dialogue.notUsed'),
          greeting.used ? 'ok' : 'bad'
        )
      : '') +
    (typeof greeting?.score === 'number' ? ` ${scoreSpan(greeting.score)}` : '');
  const greetingHtml = greeting
    ? block(
        t('records.analysisPanel.dialogue.greeting'),
        `${greetingVerdict ? `<div class="kv">${greetingVerdict}</div>` : ''}${
          greeting.feedback ? `<p>${escapeHtml(greeting.feedback)}</p>` : ''
        }`
      )
    : '';

  let bantHtml = '';
  if (hasBant) {
    const boxes = bantRows
      .map((row) => {
        const variant = row.identified ? 'ok' : 'muted';
        const scoreHtml = row.score !== undefined ? ` ${scoreSpan(row.score)}` : '';
        return (
          `<div class="box"><div class="top"><b>${escapeHtml(row.label)}</b>` +
          `${tag(row.tagLabel, variant)}${scoreHtml}</div>` +
          `${row.quote ? `<q>${escapeHtml(row.quote)}</q>` : ''}</div>`
        );
      })
      .join('');

    const totalScore = dialogue?.needsQualification?.totalScore;
    const totalScoreHtml =
      totalScore !== undefined
        ? kvRow(t('records.analysisPanel.bant.totalScore'), scoreSpan(totalScore))
        : '';
    const bantFeedback = dialogue?.needsQualification?.feedback;
    const bantFeedbackHtml = bantFeedback ? `<p>${escapeHtml(bantFeedback)}</p>` : '';

    bantHtml = block(
      t('records.analysisPanel.bantQualification'),
      `<div class="grid2">${boxes}</div>${totalScoreHtml}${bantFeedbackHtml}`
    );
  }

  let objectionsHtml = '';
  if (hasObjections) {
    const averageScore = dialogue!.objectionHandling!.averageScore;
    const boxes = objections!
      .map((obj, idx) => {
        const isLast = idx === objections!.length - 1;
        const scoreHtml = typeof obj.score === 'number' ? scoreSpan(obj.score) : '';
        // Записи, сделанные до появления балла у каждого возражения, несут
        // только исторический флаг `handled`. Без вердикта строка сводилась к
        // типу возражения и цитате клиента: неотработанное возражение
        // печаталось ровно так же, как отработанное. Вердикт берётся из общей
        // `isObjectionHandled` (@aica/core) — той же, что читают экран, CRM-
        // отчёт и движок алертов, чтобы четыре поверхности не разошлись в
        // оценке одной записи. Здесь, в отличие от BANT, вердикт красится
        // `ok`/`bad`: строкой выше в этом же блоке балл возражения уже
        // печатается цветом scoreSpan, и приглушённый бейдж рядом читался бы
        // как другая метрика.
        const handled = isObjectionHandled(obj);
        const verdictHtml =
          typeof obj.score === 'number'
            ? ''
            : ` ${tag(
                t(
                  handled
                    ? 'records.analysisPanel.dialogue.objectionHandled'
                    : 'records.analysisPanel.dialogue.objectionNotHandled'
                ),
                handled ? 'ok' : 'bad'
              )}`;
        return (
          `<div class="box"${isLast ? '' : ' style="margin-bottom:6px"'}>` +
          `<div class="top"><b>${escapeHtml(obj.type)}</b>${scoreHtml}${verdictHtml}</div>` +
          `${obj.quote ? `<q>${escapeHtml(obj.quote)}</q>` : ''}` +
          `</div>`
        );
      })
      .join('');
    // Балл красится от СЫРОГО averageScore (scoreHex), текст — округлённый
    // (Math.round). Экран (DialogueSection.tsx:163,166) делает то же самое:
    // округление касается только отображаемого числа, не банда. Нельзя
    // просто вызвать `scoreSpan(Math.round(averageScore))` — внутри него
    // ОДНО число красит и идёт в текст, а округление способно перевести
    // балл через границу scoreBand (39.6 стал бы 40 → «удовлетворительно»
    // вместо «плохо»), рассинхронизировав цвет печати с экраном.
    const avgScoreHtml =
      typeof averageScore === 'number'
        ? ` <span style="font-family:var(--mono);font-weight:600;color:${scoreHex(averageScore)}">${Math.round(averageScore)}</span>`
        : '';
    objectionsHtml =
      `<div class="blk"><div class="lbl">${escapeHtml(t('records.analysisPanel.dialogue.objectionHandling'))}` +
      `${avgScoreHtml}</div>` +
      boxes +
      `</div>`;
  }

  // `closingNotApplicable` — a print-only hint the screen doesn't have. On
  // screen a bare em-dash is enough (the rest of the panel, tooltips, and
  // the call itself sit right next to it, so a confused reader can just
  // look around). The PDF leaves the app: it's opened a week later, or by a
  // manager who never saw the panel, so a bare "—" reads as "missing data",
  // not "not applicable to this call" — the same failure `nullableScore`
  // already exists to prevent for the color (null used to paint as a red
  // zero). This is a deliberate print/screen divergence, decided with the
  // team lead (docs/plans/2026-08-06-pdf-report-preview.html:220 already
  // showed the hint verbatim) — not an oversight.
  const closingHtml = closing
    ? block(
        t('records.analysisPanel.dialogue.closingTechnique'),
        `<div class="kv">${nullableScore(
          closing.score,
          t('records.analysisPanel.dialogue.closingNotApplicable')
        )}</div>${closing.techniqueUsed ? `<p>${escapeHtml(closing.techniqueUsed)}</p>` : ''}${
          closing.feedback
            ? `<p style="font-style:italic">${escapeHtml(closing.feedback)}</p>`
            : ''
        }`
      )
    : '';

  return card({
    title: t('records.analysisPanel.dialogueDetails'),
    body: greetingHtml + bantHtml + objectionsHtml + closingHtml,
  });
};
